'use client';
import { forwardRef } from 'react';
import FlexBox from '../flex-box/index.mjs';
import Typography from '../typography/index.mjs';
import Button from '../button/index.mjs';
import TextButton from '../text-button/index.mjs';
import { useModalActionAreaContext } from '../modal/contexts.mjs';
import { ACTION_AREA_NAME, ACTION_AREA_BUTTON_NAME } from './constants.mjs';
import { ActionAreaProvider, useActionAreaContext } from './contexts.mjs';
import { actionAreaStyle, actionButtonSingle } from './style.mjs';
import { jsx, jsxs } from 'react/jsx-runtime';

// src/components/action-area/index.tsx
var ActionArea = forwardRef(
  ({
    variant = "normal",
    extraContent,
    compactContent,
    priority = "compact",
    children,
    caption,
    sticky,
    ...props
  }, ref) => {
    const modalOption = useModalActionAreaContext();
    const modalSticky = (
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      modalOption !== void 0 ? modalOption.sticky && modalOption.hasScroll : void 0
    );
    return /* @__PURE__ */ jsx(ActionAreaProvider, { priority, children: /* @__PURE__ */ jsxs(
      FlexBox,
      {
        "wds-component": "action-area",
        ref,
        flexShrink: 0,
        flexDirection: "column",
        ...props,
        sx: [
          actionAreaStyle({
            variant,
            priority,
            sticky: variant === "extra" ? false : sticky ?? modalSticky
          }),
          props.sx
        ],
        children: [
          variant === "extra" && Boolean(extraContent) && /* @__PURE__ */ jsx(
            FlexBox,
            {
              gap: "8px",
              flexDirection: "column",
              alignItems: "center",
              "data-role": "action-area-extra-content",
              sx: {
                marginBottom: "var(--wds--action-area-extra-content-margin, calc(4px + var(--wds-action-area-margin-y, 20px)))"
              },
              children: extraContent
            }
          ),
          Boolean(caption) && /* @__PURE__ */ jsx(
            Typography,
            {
              align: "center",
              variant: "label2",
              weight: "regular",
              "data-role": "action-area-caption",
              color: "palette.label.alternative",
              sx: { marginBottom: "16px" },
              children: caption
            }
          ),
          priority === "compact" && Boolean(compactContent) ? /* @__PURE__ */ jsxs(FlexBox, { justifyContent: "space-between", alignItems: "center", children: [
            /* @__PURE__ */ jsx(
              FlexBox,
              {
                flexDirection: "row",
                "data-role": "action-area-compact-content",
                children: compactContent
              }
            ),
            /* @__PURE__ */ jsx(FlexBox, { flexShrink: 0, gap: "8px", "data-role": "action-area-wrapper", children })
          ] }) : /* @__PURE__ */ jsx(
            FlexBox,
            {
              flexDirection: priority === "strong" ? "column" : "row",
              gap: "8px",
              "data-role": "action-area-wrapper",
              alignSelf: priority === "compact" ? "flex-end" : "initial",
              children
            }
          )
        ]
      }
    ) });
  }
);
ActionArea.displayName = ACTION_AREA_NAME;
var ActionAreaButton = forwardRef(
  ({ variant = "main", ...props }, ref) => {
    const { priority } = useActionAreaContext(ACTION_AREA_BUTTON_NAME);
    const renderComponent = {
      main: /* @__PURE__ */ jsx(
        Button,
        {
          ref,
          variant: priority === "single" ? "outlined" : "solid",
          color: priority === "single" ? "assistive" : "primary",
          size: "large",
          fullWidth: priority === "strong" || priority === "single",
          ...props,
          sx: [actionButtonSingle(priority), props.sx]
        }
      ),
      alternative: /* @__PURE__ */ jsx(
        Button,
        {
          ref,
          variant: "outlined",
          size: "large",
          color: priority === "strong" || priority === "neutral" ? "primary" : "secondary",
          fullWidth: priority === "strong",
          ...props,
          sx: [actionButtonSingle(priority), props.sx]
        }
      ),
      sub: priority === "strong" ? /* @__PURE__ */ jsx(
        TextButton,
        {
          ref,
          variant: "assistive",
          size: "small",
          ...props,
          sx: [
            {
              margin: "8px 0px",
              width: "fit-content",
              alignSelf: "center"
            },
            props.sx
          ]
        }
      ) : /* @__PURE__ */ jsx(
        Button,
        {
          ref,
          variant: "outlined",
          color: "secondary",
          size: "large",
          ...props,
          sx: [actionButtonSingle(priority), props.sx]
        }
      )
    };
    return renderComponent[variant];
  }
);
ActionAreaButton.displayName = ACTION_AREA_BUTTON_NAME;

export { ActionArea, ActionAreaButton };
